import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../services/api';
import { initializeSocket, disconnectSocket } from '../services/socket';

export const getAccessToken = () => {
  const stored = JSON.parse(localStorage.getItem('flexboost_user'));
  return stored?.tokens?.accessToken;
};

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedUser = localStorage.getItem('flexboost_user');
    if (storedUser) {
      const parsed = JSON.parse(storedUser);
      // FIX: Extract just the user object (handles both old and new storage formats)
      setUser(parsed.user || parsed);
      
      // ═══════════════════════════════════════════════════════════
      // AUTO-CONNECT SOCKET: If user is already logged in on page load,
      // initialize the socket connection immediately so real-time 
      // repayment updates start working right away
      // ═══════════════════════════════════════════════════════════
      const token = parsed?.tokens?.accessToken;
      if (token) {
        initializeSocket(token);
      }
    }
    setLoading(false);
  }, []);

  // End the session after five minutes without meaningful user interaction.
  useEffect(() => {
    if (!user) return undefined;
    let timeoutId;
    let loggingOut = false;
    const inactivityLimit = 5 * 60 * 1000;
    const resetTimer = () => {
      if (loggingOut) return;
      window.clearTimeout(timeoutId);
      timeoutId = window.setTimeout(async () => {
        if (loggingOut) return;
        loggingOut = true;
        try { await logout(); } catch (error) {
          disconnectSocket();
          setUser(null);
          localStorage.removeItem('flexboost_user');
        }
        window.location.assign('/login?reason=inactive');
      }, inactivityLimit);
    };
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach((eventName) => window.addEventListener(eventName, resetTimer, { passive: true }));
    resetTimer();
    return () => {
      window.clearTimeout(timeoutId);
      events.forEach((eventName) => window.removeEventListener(eventName, resetTimer));
    };
  }, [user]);

  const login = async (credentials) => {
    const res = await api.post('/auth/login', credentials);
    const userData = res.data.data;
    
    // Set user state to just the user object
    setUser(userData.user);
    
    // Store full response (user + tokens) for token access
    localStorage.setItem('flexboost_user', JSON.stringify(userData));
    
    // ═══════════════════════════════════════════════════════════
    // CONNECT SOCKET: On successful login, establish socket
    // connection so C2B auto-payments trigger instant UI updates
    // ═══════════════════════════════════════════════════════════
    const token = userData?.tokens?.accessToken;
    if (token) {
      initializeSocket(token);
    }
    
    return userData;
  };

  const logout = async () => {
    try { await api.post('/auth/logout'); } catch (e) {}
    
    // ═══════════════════════════════════════════════════════════
    // DISCONNECT SOCKET: Clean up socket connection on logout
    // to prevent memory leaks and stale event listeners
    // ═══════════════════════════════════════════════════════════
    disconnectSocket();
    
    setUser(null);
    localStorage.removeItem('flexboost_user');
  };

  const hasPermission = (module, action) => {
    if (!user?.role) return false;
    const rolePerm = user.role.permissions?.find(p => p.module === module);
    if (rolePerm?.actions?.includes(action)) return true;
    const extraPerm = user.additionalPermissions?.find(p => p.module === module);
    return !!extraPerm?.actions?.includes(action);
  };

  const value = { 
    user, 
    login, 
    logout, 
    loading, 
    hasPermission, 
    isAdmin: user?.role?.name === 'Admin' 
  };
  
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);